import { useEffect, useRef, useState } from "react";
import { useIndexedDB } from "../../../../../dexie/useIndexedDB";
import {
  calculateNetAndVat,
  getOtherValues,
  parseFormattedNumber,
} from "./utils";
import formatMoney from "../../../../../formatMoney";
import ToastSuccess from "../../../../../components/ToastSuccess";
import Form from "./Form";

const ensurePointerOrEmpty = (val) =>
  val && typeof val === "object" && val.id ? val : "";

function FormDialog({
  onSubmit,
  banks,
  object,
  onCancel,
  setState,
  receipts,
  collection,
  supplier,
  supplierField,
  ...props
}) {
  const [formData, setFormData] = useState(() => {
    return object
      ? {
          id: object.id,
          date: object.date || "",
          supplier: supplier || object.supplier || "",
          gross: parseFormattedNumber(object.gross) || 0,
          net: object.net || "",
          vat: object.vat || "",
          vatExempt: object.vatExempt || "",
          zeroRated: object.zeroRated || "",
          image: object.image || "",
          bank: ensurePointerOrEmpty(object.bank) || "",
          category: object.category || "",
          status: object.status || "draft",
        }
      : {
          id: crypto.randomUUID(),
          date: "",
          supplier: supplier || "",
          gross: 0,
          net: "",
          vat: "",
          vatExempt: "",
          zeroRated: "",
          image: "",
          bank: "",
          category: "",
          status: "draft",
        };
  });

  const [tinNo, setTinNo] = useState(object?.supplier?.tinNo || "");
  const [isShowToast, setshowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [receiptList, setReceipts] = useState([]);
  const supplierPointerRef = useRef();
  const bankPointerRef = useRef();
  const categoryRef = useRef();

  const { addItem, updateItem } = useIndexedDB(collection);

  useEffect(() => {
    if (object) {
      setFormData({
        id: object.id,
        date: object.date || "",
        supplier: supplier || object.supplier || "",
        gross: parseFormattedNumber(object.gross) || "",
        net: object.net || "",
        vat: object.vat || "",
        vatExempt: object.vatExempt || "",
        zeroRated: object.zeroRated || "",
        image: object.image || "",
        bank: ensurePointerOrEmpty(object.bank) || "",
        category: object.category || "",
        status: object.status || "draft",
      });
      setTinNo(object?.supplier?.tinNo || "");
    } else {
      resetFormData();
    }
  }, [object]);

  useEffect(() => {
    const otherValues = calculateNetAndVat(
      formData,
      formData?.supplier?.isVatable || supplier?.isVatable,
    );
    setFormData((prev) => ({
      ...prev,
      net: otherValues.net,
      vat: otherValues.vat,
    }));
  }, [
    formData.gross,
    formData.vatExempt,
    formData.zeroRated,
    formData.supplier,
    supplier,
  ]);

  useEffect(() => {
    setReceipts(receipts);
  }, [receipts]);

  const onChange = (value, field) => {
    console.log(field, value);
    if (field === "supplier") {
      setFormData((prev) => ({
        ...prev,
        supplier: value,
      }));
      setTinNo(value?.tinNo || "N/A");
    } else if (field === "bank") {
      // const selectedBank = banks?.find((bank) => bank.id === value?.id);
      const bankValue = value || ensurePointerOrEmpty(value) || "";
      setFormData((prev) => ({ ...prev, bank: bankValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otherValues = getOtherValues(
      formData,
      formData?.supplier || supplier,
    );

    const updatedObject = {
      ...formData,
      gross: formatMoney(formData.gross),
      net: otherValues.net,
      vat: otherValues.vat,
      status: "draft",
      bank: ensurePointerOrEmpty(formData.bank) || "",
      category: formData.category || "",
    };
    if (formData?.supplier || formData?.supplier === "") {
      updatedObject.supplier = supplier || formData?.supplier;
    }

    const effectiveSupplier = formData?.supplier || supplier;
    if (effectiveSupplier?.isVatable === false) {
      delete updatedObject.vatExempt;
    }

    let updatedReceipts;

    if (object) {
      await updateItem(object.id, updatedObject);
      updatedReceipts = receiptList.map((r) =>
        r.id === object.id ? updatedObject : r,
      );
      showToast("Data Successfully Updated!");
    } else {
      updatedObject.id = crypto.randomUUID();
      await addItem(updatedObject);
      updatedReceipts = [updatedObject, ...receiptList];

      resetFormData();
      showToast("Data Successfully Added!");
    }

    setReceipts(updatedReceipts);
    setState({ receipts: updatedReceipts });
  };

  const resetFormData = () => {
    setFormData({
      id: crypto.randomUUID(),
      date: "",
      supplier: "",
      gross: "",
      net: "",
      vat: "",
      vatExempt: "",
      zeroRated: "",
      image: "",
      bank: "",
      category: "",
      status: "draft",
    });
    setTinNo("");

    supplierPointerRef.current?.onClickClear?.();
    bankPointerRef.current?.onClickClear?.();
    categoryRef.current?.onClickClear?.();
  };

  function showToast(message, timeout = 1500) {
    setToastMessage(message);
    setshowToast(true);
    setTimeout(() => setshowToast(false), timeout);
  }

  return (
    <>
      <div className="m-0 card bg-white p-3">
        <div className="d-flex flex-column">
          <div style={{ borderBottom: "1px solid gray" }}>
            <p
              className="mb-1 text-primary fw-bold"
              style={{ fontSize: "0.9rem" }}
            >
              Transaction Details
            </p>
          </div>
          <Form
            supplier={supplier}
            onChange={onChange}
            formData={formData}
            setFormData={setFormData}
            tinNo={tinNo}
            handleSubmit={handleSubmit}
            supplierPointerRef={supplierField ? supplierPointerRef : null}
            bankPointerRef={bankPointerRef}
            categoryRef={categoryRef}
            supplierField={supplierField}
            {...props}
          />
        </div>
        <div
          className="toast-container position-fixed"
          style={{ transform: "translate(-50%,-50%)", top: "10%", left: "50%" }}
        >
          <ToastSuccess show={isShowToast} message={toastMessage} />
        </div>
      </div>
    </>
  );
}

export default FormDialog;
